// src/utils/messageTemplates.js
// SMS / EMAIL / VOICE CALL MESSAGE TEMPLATES — English + Spanish
// Variables: {name}, {amount}, {month}, {unit}, {installments}, {property}, {phone}
// Voice scripts include FDCPA Mini-Miranda + TCPA AI disclosure requirements.
// ${amount} is a literal dollar sign + placeholder, NOT a JS template expression.
/* eslint-disable no-template-curly-in-string */

export const templates = {

  // FRIENDLY (Low risk / pre-reminder)
  friendly: {
    en: "Hi {name}, this is {property}. Friendly reminder that rent of ${amount} is coming up soon. Please reach out if you need anything.",
    es: "Hola {name}, le escribe {property}. Este es un recordatorio amistoso de que la renta de ${amount} vence pronto. Avisenos si necesita algo.",
  },

  // FIRM (Medium risk / past due)
  firm: {
    en: "Hi {name}, this is {property}. We have not received your rent of ${amount} for {month}. Please reply today with your payment timeline so we can work through next steps together.",
    es: "Hola {name}, le escribe {property}. No hemos recibido la renta de ${amount} de {month}. Responda hoy con su fecha estimada de pago para poder ayudarle.",
  },

  // DIRECT (High risk / significantly overdue)
  direct: {
    en: "{name}, this is {property}. Your balance of ${amount} is now significantly overdue. Please pay today or reply immediately to discuss options and avoid additional action.",
    es: "{name}, le escribe {property}. Su saldo de ${amount} esta significativamente vencido. Favor de pagar hoy o responder de inmediato para revisar opciones y evitar acciones adicionales.",
  },

  // PAYMENT PLAN
  paymentPlan: {
    en: "Hi {name}, this is {property}. We can split your balance of ${amount} into {installments} manageable payments. Reply YES if you would like us to set that up.",
    es: "Hola {name}, le escribe {property}. Podemos dividir su saldo de ${amount} en {installments} pagos manejables. Responda SI si desea que lo configuremos.",
  },

  // PRE-REMINDER
  preReminder: {
    en: "Hi {name}, this is {property}. Your rent of ${amount} is due in 3 days. Please let us know if you need anything before then.",
    es: "Hola {name}, le escribe {property}. Su renta de ${amount} vence en 3 dias. Avisenos si necesita algo antes de esa fecha.",
  },

  // FOLLOW UP
  followUp: {
    en: "Hi {name}, this is {property} following up on your balance of ${amount}. We have not heard back yet. Please respond today so we can help with next steps.",
    es: "Hola {name}, le escribe {property} para dar seguimiento a su saldo de ${amount}. Aun no hemos recibido respuesta. Responda hoy para poder ayudarle con los siguientes pasos.",
  },

  // LEGAL NOTICE (Final warning before formal action)
  legalNotice: {
    en: "{name}, this is a final notice regarding your unpaid balance of ${amount}. If payment is not received within 3 days, we will be required to begin formal lease violation procedures. Please contact us immediately to avoid this outcome.",
    es: "{name}, este es un aviso final sobre tu saldo pendiente de ${amount}. Si no se recibe el pago en 3 dias, iniciaremos procedimientos formales de violacion de contrato. Comunicate con nosotros de inmediato para evitar esta situacion.",
  },

  // HARDSHIP ACK
  hardship: {
    en: "Hi {name}, this is {property}. Thank you for reaching out. A team member will review your message and follow up within 24 hours to discuss available options.",
    es: "Hola {name}, le escribe {property}. Gracias por comunicarse. Un miembro del equipo revisara su mensaje y le respondera dentro de 24 horas para revisar opciones disponibles.",
  },

  // VOICE SCRIPT — ElevenLabs AI agent opening prompt
  // Includes FDCPA Mini-Miranda + TCPA AI/bot disclosure
  voiceScript: {
    en: "Hello, may I speak with {name}? This is an automated call from {property} — I am an AI assistant, not a human, and this call may be recorded. This is an attempt to collect a debt and any information obtained will be used for that purpose. Our records show a balance of {amount} dollars that is currently past due. To make a payment, set up a payment plan, or report an error, please press 1 now. To stop receiving these automated calls, say stop at any time. Thank you.",
    es: "Hola, puede hablar con {name}? Esta es una llamada automatizada de {property}. Soy un asistente de inteligencia artificial, no una persona, y esta llamada puede ser grabada. Esta es una gestion de cobro de deuda. Nuestros registros muestran un saldo de {amount} dolares que esta vencido. Para realizar un pago, establecer un plan, o reportar un error, presione 1 ahora. Para detener estas llamadas, diga detener en cualquier momento. Gracias.",
  },

  // VOICEMAIL (Left when no answer)
  voicemail: {
    en: "Hi, this message is for {name}. This is an automated call from {property} — I am an AI assistant. This is an attempt to collect a debt. Your current balance is {amount} dollars. Please call us back or reply to our text message. To stop these calls, reply STOP to our text. Thank you.",
    es: "Hola, este mensaje es para {name}. Esta es una llamada automatizada de {property} — soy un asistente de IA. Esta es una gestion de cobro de deuda. Su saldo actual es {amount} dolares. Llamenos o responda a nuestro texto. Para detener estas llamadas, responda ALTO. Gracias.",
  },
};


/** Fill template variables with tenant data. */
export function fillTemplate(templateKey, tenant, lang = 'en', extras = {}) {
  const template = templates[templateKey];
  if (!template) return '';
  let message = template[lang] || template['en'];
  const now = new Date();
  const locale = lang === 'es' ? 'es-US' : 'en-US';
  const monthName = now.toLocaleString(locale, { month: 'long' });
  message = message
    .replace(/{name}/g,         tenant?.name || 'Resident')
    .replace(/{amount}/g,       (tenant?.currentBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }))
    .replace(/{month}/g,        monthName)
    .replace(/{unit}/g,         tenant?.unit || '')
    .replace(/{installments}/g, tenant?.installments || '2')
    .replace(/{property}/g,     extras.property || 'your property management company')
    .replace(/{phone}/g,        extras.phone || 'the office');
  return message;
}


/** Get the recommended template key for a tier + prior outreach count. */
export function getTemplateForTier(tier, outreachCount = 0) {
  switch (tier) {
    case 'high':    return outreachCount >= 2 ? 'legalNotice' : outreachCount >= 1 ? 'direct' : 'firm';
    case 'medium':  return outreachCount >= 1 ? 'firm' : 'friendly';
    case 'low':     return 'friendly';
    case 'minimal': return 'preReminder';
    default:        return 'friendly';
  }
}

/** SMS/text template keys (voice-only excluded). */
export const SMS_TEMPLATE_KEYS = [
  'friendly', 'firm', 'direct', 'paymentPlan',
  'preReminder', 'followUp', 'legalNotice', 'hardship',
];

/** Human-readable labels for all template keys. */
export const TEMPLATE_LABELS = {
  friendly:    'Friendly',
  firm:        'Firm',
  direct:      'Direct',
  paymentPlan: 'Payment Plan',
  preReminder: 'Pre-Reminder',
  followUp:    'Follow Up',
  legalNotice: 'Legal Notice',
  hardship:    'Hardship',
  voiceScript: 'Voice Script',
  voicemail:   'Voicemail',
};
